import { inject } from '@angular/core';
import { CanActivateChildFn, Router, RouterStateSnapshot } from '@angular/router';
import { catchError, combineLatest, map, of, take } from 'rxjs';
import { UserService } from '../../user/user.service';
import { TenantService } from '../../../modules/administration/tenant/tenant.service';
import { pathRequiresTenant } from './tenant-exempt-paths';

/**
 * Blocks business-module routes when the authenticated user has no tenant.
 *
 * Runs as `canActivateChild` on the authenticated shell, AFTER `authGuard`. Decision table
 * (zero-tenant only — with a tenant it always allows):
 *   - route is exempt (administration/profile/... — see tenant-exempt-paths) → allow.
 *   - admin  → /administration/tenant (the list, which hosts the create dialog + wizard).
 *   - non-admin → /system-not-initialized (contact-admin screen; no create CTA).
 *   - tenant fetch ERRORS (network/500) → /server-error. A failure is NOT "no tenant";
 *     mis-routing it into onboarding would hide a real outage (DECISION A).
 *
 * The redirect targets are all exempt or outside this shell, so the guard never loops:
 *   /administration/tenant  → 'administration' is exempt
 *   /system-not-initialized → sibling route outside the shell (no tenantGuard)
 *   /server-error           → sibling route outside the shell (no tenantGuard)
 */
export const tenantGuard: CanActivateChildFn = (_childRoute, state: RouterStateSnapshot) => {
  const userService = inject(UserService);
  const tenantService = inject(TenantService);
  const router = inject(Router);

  if (!pathRequiresTenant(state.url)) {
    return of(true);
  }

  return combineLatest([userService.isAdmin$, tenantService.ensureLoaded()]).pipe(
    take(1),
    map(([isAdmin, tenants]) => {
      const hasTenant = (tenants?.length ?? 0) > 0;
      if (hasTenant) {
        return true;
      }
      return isAdmin
        ? router.parseUrl('/administration/tenant')
        : router.parseUrl('/system-not-initialized');
    }),
    catchError(() => of(router.parseUrl('/server-error'))),
  );
};
